import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button } from '@lms/ui';
import { Calendar, AlertCircle, User, BookOpen } from 'lucide-react';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import type { Announcement, AnnouncementCategory, AnnouncementPriority } from '@lms/shared/types';

interface AnnouncementDetailModalProps {
  announcement: Announcement | null;
  isOpen: boolean;
  onClose: () => void;
}

export function AnnouncementDetailModal({
  announcement,
  isOpen,
  onClose,
}: AnnouncementDetailModalProps) {
  const { language, t } = useLanguage();

  if (!announcement) return null;

  const getPriorityBadge = (priority: AnnouncementPriority) => {
    switch (priority) {
      case 'URGENT':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-error-container text-on-error-container">
            <AlertCircle size={12} />
            {t(copy.announcements.prioUrgent)}
          </span>
        );
      case 'HIGH':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-warning-container text-on-warning-container">
            {t(copy.announcements.prioHigh)}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-surface-container-high text-on-surface-variant">
            {t(copy.announcements.prioNormal)}
          </span>
        );
    }
  };

  const getCategoryBadge = (category: AnnouncementCategory) => {
    switch (category) {
      case 'ACADEMIC':
        return t(copy.announcements.catAcademic);
      case 'IMPORTANT':
        return t(copy.announcements.catImportant);
      case 'MAINTENANCE':
        return t(copy.announcements.catMaintenance);
      case 'EVENT':
        return t(copy.announcements.catEvent);
      default:
        return t(copy.announcements.catGeneral);
    }
  };

  const formatDate = (date: Date | string | undefined | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-label font-bold uppercase tracking-wider bg-primary/10 text-primary px-2.5 py-0.5 rounded-full">
              {getCategoryBadge(announcement.category)}
            </span>
            {getPriorityBadge(announcement.priority)}
          </div>
          <DialogTitle className="text-xl sm:text-2xl font-headline font-extrabold text-on-surface leading-tight">
            {announcement.title}
          </DialogTitle>
          <div className="flex flex-wrap items-center gap-4 text-xs font-body text-on-surface-variant opacity-75 pt-1">
            <span className="flex items-center gap-1.5">
              <Calendar size={13} />
              {formatDate(announcement.createdAt)}
            </span>
            {announcement.program && (
              <span className="flex items-center gap-1.5 text-primary font-bold">
                <BookOpen size={13} />[{announcement.program.code}] {announcement.program.name}
              </span>
            )}
            {announcement.author && (
              <span className="flex items-center gap-1.5">
                <User size={13} />
                {announcement.author.name}
              </span>
            )}
          </div>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="p-5 bg-surface-container-low/70 rounded-2xl text-sm font-body text-on-surface leading-relaxed whitespace-pre-wrap">
            {announcement.content}
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="px-6 py-2 rounded-xl text-xs font-label font-bold uppercase tracking-wider"
          >
            <Bilingual {...copy.common.close} />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
